import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup, onAuthStateChanged } from 'firebase/auth';
import { getDatabase, ref, set } from 'firebase/database';
import emailjs from 'emailjs-com';
import Swal from 'sweetalert2';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loader from 'react-spinners/FadeLoader';

import { auth } from './Config';

import './SignIn.css';

const SignIn = ({ darkmodeClass, setAuthenticated }) => {

  const [inputData, setInputData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    otp: '',
    enteredOtp: '',
  });

  const [errors, setErrors] = useState({
    firstNameError: '',
    lastNameError: '',
    emailError: '',
    phoneNumberError: '',
    passwordError: '',
    confirmPasswordError: '',
    signInError: '',
    otpVerificationError: '',
    registrationError: '',
  });

  const [generalState, setGeneralState] = useState({
    registerButtonDisabled: true,
    isSignInMode: true,
    isPhoneNumberCorrect: false,
    isOtpSent: false,
    isOtpSendButtonVisible: false,
    isOtpVerified: false,
    isEmailVerified: false,
  });

  const [loading, setLoading] = useState(false);
  const [loadingSendOtp, setLoadingSendOtp] = useState(false);
  const [loadingVerifyOtp, setLoadingVerifyOtp] = useState(false);

  const navigate = useNavigate();

  const validateForm = () => {
    const isValidFirstName = /^[A-Za-z ]+$/.test(inputData.firstName);
    const isValidLastName = /^[A-Za-z ]+$/.test(inputData.lastName);
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inputData.email);
    const isValidPhoneNumber = inputData.phoneNumber.length === 10 || inputData.phoneNumber === '';
    const hasNumber = /\d/.test(inputData.password);
    const hasCharacter = /[a-zA-Z]/.test(inputData.password);
    const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(inputData.password);
    const isValidPassword = inputData.password.length >= 8 && hasNumber && hasCharacter && hasSymbol;
    const isValidConfirmPassword = inputData.confirmPassword === inputData.password;

    return {
      isValidFirstName,
      isValidLastName,
      isValidEmail,
      isValidPhoneNumber,
      isValidPassword,
      isValidConfirmPassword,
    };
  };


  const handleInputChange = (field, value) => {
    setInputData((prevInputData) => ({
      ...prevInputData,
      [field]: value,
    }));
  };

  const handleFirstNameChange = (e) => {
    const newFirstName = e.target.value;
    handleInputChange('firstName', newFirstName);
    setErrors((prevErrors) => ({
      ...prevErrors,
      firstNameError: validateForm().isValidFirstName ? '' : 'Enter a valid first name',
    }));
    updateRegisterButtonDisabled();
  };

  const handleLastNameChange = (e) => {
    const newLastName = e.target.value;
    handleInputChange('lastName', newLastName);
    setErrors((prevErrors) => ({
      ...prevErrors,
      lastNameError: validateForm().isValidLastName ? '' : 'Enter a valid last name',
    }));
    updateRegisterButtonDisabled();
  };

  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    handleInputChange('email', newEmail);
    setErrors((prevErrors) => ({
      ...prevErrors,
      emailError: validateForm().isValidEmail ? '' : 'Enter a valid email address',
    }));
    setGeneralState((prevGeneralState) => ({
      ...prevGeneralState,
      isOtpSendButtonVisible: validateForm().isValidEmail && !prevGeneralState.isOtpSent && !prevGeneralState.isOtpVerified,
      isOtpVerified: false,
      isOtpSent: false,
    }));
    updateRegisterButtonDisabled();
  };

  const handlePhoneNumberChange = (e) => {
    const enteredValue = e.target.value;
    const formattedPhoneNumber = enteredValue.slice(0, 10);
    handleInputChange('phoneNumber', formattedPhoneNumber);

    const isValidPhoneNumber = formattedPhoneNumber.length === 10 || formattedPhoneNumber === '';
    setErrors((prevErrors) => ({
      ...prevErrors,
      phoneNumberError: isValidPhoneNumber ? '' : 'Enter a valid 10-digit phone number',
    }));

    const isPhoneNumberCorrect = isValidPhoneNumber && formattedPhoneNumber.length === 10;
    setGeneralState((prevGeneralState) => ({
      ...prevGeneralState,
      isPhoneNumberCorrect: isPhoneNumberCorrect,
    }));

    updateRegisterButtonDisabled();
  };


  const handlePasswordChange = (e) => {
    const enteredPassword = e.target.value;

    setInputData((prevInputData) => ({
      ...prevInputData,
      password: enteredPassword,
    }));

    const hasNumber = /\d/.test(enteredPassword);
    const hasCharacter = /[a-zA-Z]/.test(enteredPassword);
    const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(enteredPassword);
    const isValidPassword =
      enteredPassword.length >= 8 && hasNumber && hasCharacter && hasSymbol;

    setErrors((prevErrors) => ({
      ...prevErrors,
      passwordError: isValidPassword
        ? inputData.confirmPassword && enteredPassword !== inputData.confirmPassword
          ? 'Passwords do not match'
          : ''
        : 'Password must be at least 8 characters and contain at least one number, one character, and one symbol',
    }));

    if (generalState.isSignInMode) {
      setGeneralState((prevGeneralState) => ({
        ...prevGeneralState,
        registerButtonDisabled: !enteredPassword || enteredPassword.length <= 8,
      }));
    } else {
      updateRegisterButtonDisabled();
    }
  };


  const handleConfirmPasswordChange = (e) => {
    const enteredConfirmPassword = e.target.value;
    setInputData((prevInputData) => ({
      ...prevInputData,
      confirmPassword: enteredConfirmPassword,
    }));

    const isValidPassword = enteredConfirmPassword === inputData.password;

    setErrors((prevErrors) => ({
      ...prevErrors,
      confirmPasswordError: isValidPassword ? '' : 'Passwords do not match',
    }));

    updateRegisterButtonDisabled();
  };

  // Function to generate random OTP
  const generateOTP = () => {
    return Math.floor(100000 + Math.random() * 900000);
  };


  const sendOtpEmail = async () => {
    setLoadingSendOtp(true);
    const generatedOtp = generateOTP();

    setInputData((prevInputData) => ({
      ...prevInputData,
      otp: generatedOtp.toString(),
    }));

    const templateParams = {
      to_email: inputData.email,
      subject: 'OTP Verification',
      message: generatedOtp.toString(),
    };

    try {
      await emailjs.send('service_qxt0jcf', 'template_3m5scyq', templateParams, 'LtaxEWT_VFz6n5lq4');
      setGeneralState((prevGeneralState) => ({
        ...prevGeneralState,
        otp: generatedOtp.toString(),
        isOtpSent: true,
      }));
      toast.success(<SuccessToast title="OTP Sent!!!" text="OTP has been sent to your email successfully." />, {
        position: 'top-center',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    } catch (error) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        otpVerificationError: 'Failed to send OTP. Please try again.',
      }));
    } finally {
      setLoadingSendOtp(false);
    }
  };

  const handleOtpVerification = () => {
    setLoadingVerifyOtp(true);
    if (inputData.otp === inputData.enteredOtp) {
      setGeneralState((prevGeneralState) => ({
        ...prevGeneralState,
        isOtpVerified: true,
        isEmailVerified: true,
      }));

      toast.success(<SuccessToast title="OTP Verified!!!" text="OTP has been verified successfully." />, {
        position: 'top-center',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });

    } else {
      setErrors((prevErrors) => ({
        ...prevErrors,
        otpVerificationError: 'Invalid OTP. Please enter the correct OTP.',
      }));
    }
    updateRegisterButtonDisabled();
    setLoadingVerifyOtp(false);
  };

  const updateRegisterButtonDisabled = () => {
    const formValidation = validateForm();

    if (generalState.isSignInMode) {
      const isFormValid =
        formValidation.isValidEmail &&
        formValidation.isValidPassword !== formValidation.isValidConfirmPassword;
      setGeneralState((prevGeneralState) => ({
        ...prevGeneralState,
        registerButtonDisabled: !isFormValid,
      }));
    } else {
      const isFormValid =
        formValidation.isValidFirstName &&
        formValidation.isValidLastName &&
        formValidation.isValidEmail &&
        formValidation.isValidPhoneNumber &&
        formValidation.isValidPassword !== formValidation.isValidConfirmPassword;
      setGeneralState((prevGeneralState) => ({
        ...prevGeneralState,
        registerButtonDisabled: !isFormValid,
      }));
    }
  };



  const handleSignInToggle = () => {
    setGeneralState((prevGeneralState) => ({
      ...prevGeneralState,
      isSignInMode: !prevGeneralState.isSignInMode,
      signInError: '',
    }));
  };

  const handleAuthAction = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors(() => ({
      signInError: '',
      registrationError: '',
      otpVerificationError: '',
    }));

    const {
      isValidFirstName,
      isValidLastName,
      isValidEmail,
      isValidPhoneNumber,
      isValidPassword,
    } = validateForm();

    try {
      if (generalState.isSignInMode) {
        if (!inputData.email || !inputData.password) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            signInError: 'Please fill in all required fields.',
          }));
          return;
        }

        await signInWithEmailAndPassword(auth, inputData.email, inputData.password);
        Swal.fire({
          icon: 'success',
          title: 'Welcome back!!!',
          text: 'You have successfully logged in. Redirecting...',
          showConfirmButton: false,
          timer: 2000,
        });
        setGeneralState((prevGeneralState) => ({
          ...prevGeneralState,
          isAuthenticated: true,
        }));
        navigate('/');
      } else {
        if (
          !isValidFirstName ||
          !isValidLastName ||
          !isValidEmail ||
          !isValidPhoneNumber ||
          !isValidPassword ||
          inputData.password !== inputData.confirmPassword
        ) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            registrationError: 'Please fill in all required fields with valid information.',
          }));
          return;
        }

        if (!generalState.isOtpVerified) {
          setErrors((prevErrors) => ({
            ...prevErrors,
            registrationError: 'Please verify your email via OTP before signing up.',
          }));
          return;
        }

        // Create user in Firebase Authentication
        await createUserWithEmailAndPassword(auth, inputData.email, inputData.password);

        // Store user data in the database
        const database = getDatabase();
        const userRef = ref(database, `users/${inputData.email.replace('.', '_')}`);
        const userData = {
          firstName: inputData.firstName,
          lastName: inputData.lastName,
          email: inputData.email,
          phoneNumber: inputData.phoneNumber,
          password: inputData.password,
          confirmPassword: inputData.confirmPassword,
        };
        await set(userRef, userData);
        Swal.fire({
          icon: 'success',
          title: 'Registration Successful!!!',
          text: 'You have successfully registered and logged in. Redirecting...',
          showConfirmButton: false,
          timer: 2000,
        });
        setGeneralState((prevGeneralState) => ({
          ...prevGeneralState,
          isAuthenticated: true,
        }));
        navigate('/');
      }
    } catch (error) {
      let errorMessages = [];

      if (error.code === 'auth/invalid-email') {
        errorMessages.push('Invalid email format. Please enter a valid email address.');
      }

      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        errorMessages.push('Invalid email or password. Please check your credentials.');
      }

      if (error.code === 'auth/invalid-credential') {
        errorMessages.push('Invalid credential. Please check your information.');
      }

      if (errorMessages.length === 0) {
        errorMessages.push('Authentication failed. Please try again.');
      }

      setErrors((prevErrors) => ({
        ...prevErrors,
        signInError: errorMessages.join(' '),
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
      Swal.fire({
        icon: 'success',
        title: 'Welcome!',
        text: 'Google Sign-In successful. Thank you for joining us. Redirecting...',
        showConfirmButton: false,
        timer: 2000,
      });
      setGeneralState((prevGeneralState) => ({
        ...prevGeneralState,
        isAuthenticated: true,
      }));
      navigate('/');
    } catch (error) {
      toast.error(`Google Sign-In failed: ${error.message}`);
    }
  };

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        // User is signed in
        setGeneralState((prevGeneralState) => ({
          ...prevGeneralState,
          isAuthenticated: true,
        }));
        navigate('/');
      }
    });

    // Cleanup function to unsubscribe from the auth state changes when the component unmounts
    return () => unsubscribe();
  }, [navigate, setGeneralState]);

  useEffect(() => {
    // Clear the timeout when the component unmounts or when successMessage changes
    return () => {
      clearTimeout();
    };
  }, []);

  const SuccessToast = ({ title, text }) => (
    <div>
      <h4>{title}</h4>
      <p>{text}</p>
    </div>
  );


  return (
    <div className={`signInContainer ${darkmodeClass} ${loading ? 'loading-opacity' : ''}`}>
      <div className={`formContainer ${darkmodeClass}`}>
        {loading && (
        <div className="sweet-loading">
          <p className={`loadingp ${darkmodeClass}`}>Signing In...</p>
          <Loader color='#36d7b7' size={100} loading={loading} />
        </div>)}

          <form onSubmit={handleAuthAction}>
            {generalState.isSignInMode ? (
              <div className='formsignin'>
                <div className={`heading ${darkmodeClass}`}>
                  <h2>Sign In</h2>
                </div>
                <hr className='hr' />
                <div className='fielddiv1'>
                  <label className='emaillabel'>
                    <p>Email:<span className='required'>*</span></p>
                    <input
                      className={`placeholder-text ${darkmodeClass}`}
                      placeholder='Enter Your Email'
                      type="email"
                      value={inputData.email}
                      onChange={handleEmailChange}
                    />
                  </label>
                  <p className={`error ${errors.emailError ? 'visible' : 'hidden'}`}>{errors.emailError}</p>
                </div>
                <div className='fielddiv1'>
                  <label className='pswlabel'>
                    <p>Password:<span className='required'>*</span></p>
                    <input
                      className={`placeholder-text ${darkmodeClass}`}
                      placeholder='Enter Your Password'
                      type="password"
                      value={inputData.password}
                      onChange={handlePasswordChange}
                    />
                  </label>
                  <p className={`error ${errors.passwordError ? 'visible' : 'hidden'}`}>{errors.passwordError}</p>
                </div>
                <div className='signdiv'>
                  <button className='sign1' type="submit" disabled={generalState.registerButtonDisabled}>
                    Sign In
                  </button>
                  <p className={`error ${errors.signInError ? 'visible' : 'hidden'}`}>{errors.signInError}</p>
                </div>
              </div>
            ) : (
              <div className='register1'>
                <div className='form'>
                  <div>
                    <div className={`heading ${darkmodeClass}`}>
                      <h2>Registering New User</h2>
                    </div>
                    <hr className='hr' />
                    <div className='formcontent'>
                      <div className='fielddiv'>
                        <label>
                          <p>First Name:<span className='required'>*</span></p>
                          <input
                            className={`placeholder-text ${darkmodeClass}`}
                            placeholder='Enter First Name'
                            type="text"
                            value={inputData.firstName}
                            onChange={handleFirstNameChange}
                          />
                        </label>
                        <p className={`error ${errors.firstNameError ? 'visible' : 'hidden'}`}>{errors.firstNameError}</p>
                      </div>
                      <div className='fielddiv'>
                        <label>
                          <p>Last Name:<span className='required'>*</span></p>
                          <input
                            className={`placeholder-text ${darkmodeClass}`}
                            placeholder='Enter Last Name'
                            type="text"
                            value={inputData.lastName}
                            onChange={handleLastNameChange}
                          />
                        </label>
                        <p className={`error ${errors.lastNameError ? 'visible' : 'hidden'}`}>{errors.lastNameError}</p>
                      </div>
                    </div>
                    <div className='formcontent'>
                      <div className='fielddiv'>
                        <label className={`${generalState.isOtpVerified ? 'verified' : ''}`}>
                          <p>Email:<span className='required'>*</span></p>
                          <input
                            className={`placeholder-text ${darkmodeClass}`}
                            placeholder='Enter Your Email'
                            type="email"
                            value={inputData.email}
                            onChange={handleEmailChange}
                          />

                          {loadingSendOtp ? (
                            <div className="loadingSendOtp">
                              <p className={`otpsendp ${darkmodeClass}`}>Sending OTP...</p>
                            </div>
                          ) : (
                            generalState.isOtpSendButtonVisible && !generalState.isOtpSent && (
                              <div className='sendotpdiv'>
                                <button className='sendOtp' type="button" onClick={sendOtpEmail}>
                                  Send OTP
                                </button>
                              </div>
                            ))}
                          {generalState.isOtpSent && !generalState.isOtpVerified && (
                            <div className='verifyContainer'>
                              <label>
                                <p>Enter OTP:</p>
                              </label>
                              <div className='verifydiv'>
                                <input
                                  className={`placeholder-text ${darkmodeClass}`}
                                  placeholder='Enter OTP'
                                  type="text"
                                  value={inputData.enteredOtp}
                                  onChange={(e) => setInputData({ ...inputData, enteredOtp: e.target.value })}
                                />

                                <button
                                  className={`verifyOtp ${generalState.isOtpVerified ? 'disabled' : ''}`}
                                  type="button"
                                  onClick={handleOtpVerification}
                                  disabled={generalState.isOtpVerified}
                                >
                                  {loadingVerifyOtp ? (
                                     <p className={`otpsendp ${darkmodeClass}`}>Verifying OTP...</p>
                                  ) : (
                                    generalState.isOtpVerified ? 'Verified' : 'Verify'
                                  )}
                                </button>
                              </div>
                              <p className={`error ${errors.otpVerificationError ? 'visible' : 'hidden'}`}>{errors.otpVerificationError}</p>
                            </div>
                          )}
                        </label>
                        <p className={`error ${errors.emailError ? 'visible' : 'hidden'}`}>{errors.emailError}</p>
                      </div>
                      <div className='fielddiv'>
                        <label>
                          <p>Phone Number:<span className='required'>*</span></p>
                          <input
                            className={`placeholder-text ${darkmodeClass}`}
                            placeholder='Enter Phone Number'
                            type="tel"
                            value={inputData.phoneNumber}
                            onChange={handlePhoneNumberChange}
                          />
                        </label>
                        <p className={`error ${errors.phoneNumberError ? 'visible' : 'hidden'}`}>{errors.phoneNumberError}</p>
                      </div>
                    </div>
                    <div className='formcontent psw'>
                      <div className='fielddiv'>
                        <label>
                          <p>Password:<span className='required'>*</span></p>
                          <input
                            className={`placeholder-text ${darkmodeClass}`}
                            placeholder='Enter Your Password'
                            type="password"
                            value={inputData.password}
                            onChange={handlePasswordChange}
                          />
                        </label>
                        <p className={`error ${errors.passwordError ? 'visible' : 'hidden'}`}>{errors.passwordError}</p>
                      </div>
                      <div className='fielddiv'>
                        <label>
                          <p>Confirm Password:<span className='required'>*</span></p>
                          <input
                            className={`placeholder-text ${darkmodeClass}`}
                            placeholder='Confirm Your Password'
                            type="password"
                            value={inputData.confirmPassword}
                            onChange={handleConfirmPasswordChange}
                          />
                        </label>
                        <p className={`error ${errors.confirmPasswordError ? 'visible' : 'hidden'}`}>{errors.confirmPasswordError}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='signupdiv'>
                  <button className='signup' type="submit" disabled={generalState.registerButtonDisabled}>
                    Sign Up
                  </button>
                  <p className={`error ${errors.registrationError ? 'visible' : 'hidden'}`}>{errors.registrationError}</p>
                </div>
              </div>
            )}

            <hr className='hr' />

            <div className='formbottom'>
              <div className='google'>
                <span>Sign In with: </span>
                <img
                  className='img'
                  onClick={handleGoogleSignIn}
                  src={'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c1/Google_%22G%22_logo.svg/1024px-Google_%22G%22_logo.svg.png'}
                  alt="Google Logo"
                />
              </div>
              <div className='register'>
                <p>
                  {generalState.isSignInMode
                    ? "Don't have an account? "
                    : 'Already have an account? '}
                  <button type="button" onClick={handleSignInToggle}>
                    {generalState.isSignInMode ? 'Register User' : 'Sign In'}
                  </button>
                </p>
              </div>
            </div>
          </form>
        

      </div>
      <ToastContainer
        position="top-center"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};

export default SignIn;

